'use client'

import React from 'react'
import { cn } from '@/utilities/cn'
import { useTranslations } from 'next-intl'
import { motion } from 'framer-motion'

interface ProductBannerProps {
  children: React.ReactNode
  className?: string
}

export const ProductBanner: React.FC<ProductBannerProps> = ({ children, className }) => {
  return (
    <div className={cn('absolute bottom-3 left-3 right-24 z-10 flex flex-col gap-2 items-start', className)}>
      {children}
    </div>
  )
}

interface VariantBannerProps {
  className?: string
}

export const VariantBanner: React.FC<VariantBannerProps> = ({ className }) => {
  const t = useTranslations('shop')
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={cn(
        'flex items-center justify-center px-2 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider text-white bg-blue-600/90 shadow-lg backdrop-blur-sm transition-colors',
        'group-hover:bg-blue-600 group-hover:scale-[1.02] overflow-hidden max-w-full',
        className,
      )}
    >
      <span className="truncate block w-full text-center">{t('variants')}</span>
    </motion.div>
  )
}

interface BulkDiscountBannerProps {
  className?: string
}

export const BulkDiscountBanner: React.FC<BulkDiscountBannerProps> = ({ className }) => {
  const t = useTranslations('shop')
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={cn(
        'flex items-center justify-center px-2 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider text-white bg-purple-600/90 shadow-lg backdrop-blur-sm transition-colors',
        'group-hover:bg-purple-500 group-hover:scale-[1.02] overflow-hidden max-w-full',
        className,
      )}
    >
      <span className="truncate block w-full text-center">{t('bulkDiscount')}</span>
    </motion.div>
  )
}

interface DiscountRibbonProps {
  discountPercentage: number
  className?: string
}

export const DiscountRibbon: React.FC<DiscountRibbonProps> = ({
  discountPercentage,
  className,
}) => {
  return (
    <div className={cn('absolute top-0 left-0 z-20 overflow-hidden w-20 h-20', className)}>
      <motion.div
        initial={{ opacity: 0, rotate: -45, y: -20 }}
        animate={{ opacity: 1, rotate: -45, y: 0 }}
        className={cn(
          'bg-red-600 text-white text-micro font-black py-1 w-[140%] text-center shadow-xl border-b border-red-400/50',
          'absolute top-4 -left-8 transform transition-all duration-500 group-hover:bg-red-500 group-hover:scale-110',
        )}
      >
        -{discountPercentage}%
      </motion.div>
    </div>
  )
}
